import React, { useMemo, useState, useEffect } from "react";
import AddTaskModal from "../components/AddTaskModal";
import { Filter, Search, X } from 'lucide-react';
import api from '../api/api';
import { useTheme } from '../hooks/useTheme';
import ActionItemsSkeleton from '../components/layout/ActionItemsSkeleton';

const btnSolid = "rounded-lg bg-primary-700 text-white px-4 py-2 text-sm font-semibold hover:bg-primary-800 transition-colors";
const btnOutline = (isDarkMode) => isDarkMode 
  ? "rounded-lg bg-slate-700 text-slate-200 border border-slate-600 px-4 py-2 text-sm font-semibold hover:bg-slate-600 transition-colors"
  : "rounded-lg bg-amber-100 text-amber-900 px-4 py-2 text-sm font-semibold hover:bg-amber-200 transition-colors";

function DifficultyPill({ level, isDarkMode = false }) {
  if (level === "Hard") return <span className={(isDarkMode)}>Hard</span>;
  if (level === "Medium") return <span className={(isDarkMode)}>Medium</span>;
  if (level === "Easy") return <span className={(isDarkMode)}>Easy</span>;
  return null;
}

function TagPill({ label, isDarkMode = false }) {
  if (!label) return null;
  return <span className={(isDarkMode)}>{label}</span>;
}

function DueChip({ due, isDarkMode = false }) {
  if (!due) return null;
  return <span className={(isDarkMode)}>Due: {due}</span>;
}

// function Tabs({ tabs, active, setActive, isDarkMode = false }) {
//   return (
//     <div className={`sticky top-0 z-10 mb-6 backdrop-blur ${
//       isDarkMode ? 'bg-slate-900/80 supports-[backdrop-filter]:bg-slate-900/60' : 'bg-white/80 supports-[backdrop-filter]:bg-white/60'
//     }`}>
//       <div className="flex items-center gap-2 overflow-x-auto p-2 pl-0">
//         {tabs.map((t) => (
//           <button
//             key={t}
//             type="button"
//             onClick={() => setActive(t)}
//             className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
//               active === t
//                 ? "bg-primary-700 text-white border-primary-700"
//                 : isDarkMode
//                   ? "bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700"
//                   : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
//             }`}
//           >
//             {t}
//           </button>
//         ))}
//       </div>
//     </div>
//   );
// }
// Main component for Action Items page

export default function ActionItems() {
  const { isDarkMode } = useTheme();
  const [active, setActive] = useState("All Tasks");
  const [checked, setChecked] = useState({});
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingTaskId, setDeletingTaskId] = useState(null);
  const [pendingDeleteTask, setPendingDeleteTask] = useState(null);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'thisWeek', 'thisMonth', 'custom'
  const [focusAreaFilter, setFocusAreaFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('current'); // 'current' (hide completed) | 'all' (include completed/past)
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingStatusTaskId, setUpdatingStatusTaskId] = useState(null);
  const hasBackendTasks = Array.isArray(tasks) && tasks.length > 0;

  const getTaskIdentifier = (task) => {
    return (
      task?.TaskID ||
      task?.taskID ||
      task?.taskId ||
      task?.task_id ||
      task?.id ||
      null
    );
  };

  // const tabs = ["All Tasks", "Performance", "Skilling", "Networking", "Giving Back", "Feedback"];

  const normalizedTasks = useMemo(() => {
    const sourceTasks = Array.isArray(tasks) ? tasks : [];

    if (sourceTasks.length === 0) {
      return [];
    }

    return sourceTasks
      .map((task, index) => {
        if (!task || typeof task !== 'object') return null;

        const title =
          task.Title ||
          task.title ||
          task.TaskTitle ||
          task.taskTitle ||
          task.TaskDesc ||
          task.Description;

        if (!title) return null;

        const rawDifficulty = task.DifficultyLevel || task.difficulty || task.Difficulty || 'Medium';
        const difficultyMap = {
          S: 'Small',
          M: 'Medium',
          L: 'Large',
          E: 'Small',
          H: 'Large',
          SMALL: 'Small',
          MEDIUM: 'Medium',
          LARGE: 'Large',
          EASY: 'Small',
          HARD: 'Large'
        };
        const normalizedDifficulty = difficultyMap[String(rawDifficulty).trim().toUpperCase()] || String(rawDifficulty);

        const rawTaskId = getTaskIdentifier(task);

        return {
          TaskID: rawTaskId || `${title}-${index}`,
          BackendTaskID: rawTaskId ? String(rawTaskId) : null,
          Title: String(title),
          Description: task.Description || task.description || task.TaskDesc || '',
          FocusAreaName: task.FocusAreaName || task.focusAreaName || task.tag || 'Uncategorized',
          DifficultyLevel: normalizedDifficulty,
          StartDate: task.StartDate || task.startDate || task.start || null,
          EndDate: task.EndDate || task.endDate || task.end || null,
          DueDate: task.DueDate || task.dueDate || task.due || null
          ,
          Status: task.Status || task.status || 'In Progress'
        };
      })
      .filter(Boolean);
  }, [tasks]);

  const availableFocusAreas = useMemo(() => {
    return Array.from(new Set(normalizedTasks.map((task) => task.FocusAreaName).filter(Boolean))).sort();
  }, [normalizedTasks]);

  // Transform backend tasks into sections format
  const sections = useMemo(() => {
    if (!normalizedTasks || normalizedTasks.length === 0) return [];

    // Apply date filter
    let filteredTasks = normalizedTasks;
    const now = new Date();
    
    if (dateFilter === 'today') {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      filteredTasks = normalizedTasks.filter(task => {
        const taskDate = task.EndDate ? new Date(task.EndDate) : (task.DueDate ? new Date(task.DueDate) : null);
        if (!taskDate) return false;
        return taskDate.toDateString() === today.toDateString();
      });
    } else if (dateFilter === 'thisWeek') {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay());
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);
      
      filteredTasks = normalizedTasks.filter(task => {
        const taskDate = task.EndDate ? new Date(task.EndDate) : (task.DueDate ? new Date(task.DueDate) : null);
        if (!taskDate) return false;
        return taskDate >= weekStart && taskDate <= weekEnd;
      });
    } else if (dateFilter === 'thisMonth') {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      
      filteredTasks = normalizedTasks.filter(task => {
        const taskDate = task.EndDate ? new Date(task.EndDate) : (task.DueDate ? new Date(task.DueDate) : null);
        if (!taskDate) return false;
        return taskDate >= monthStart && taskDate <= monthEnd;
      });
    } else if (dateFilter === 'custom' && customDateStart && customDateEnd) {
      const start = new Date(customDateStart);
      start.setHours(0, 0, 0, 0);
      const end = new Date(customDateEnd);
      end.setHours(23, 59, 59, 999);
      
      filteredTasks = normalizedTasks.filter(task => {
        const taskDate = task.EndDate ? new Date(task.EndDate) : (task.DueDate ? new Date(task.DueDate) : null);
        if (!taskDate) return false;
        return taskDate >= start && taskDate <= end;
      });
    }

    if (focusAreaFilter !== 'all') {
      filteredTasks = filteredTasks.filter(
        (task) => String(task.FocusAreaName).toLowerCase() === String(focusAreaFilter).toLowerCase()
      );
    }

    if (difficultyFilter !== 'all') {
      filteredTasks = filteredTasks.filter(
        (task) => String(task.DifficultyLevel).toLowerCase() === String(difficultyFilter).toLowerCase()
      );
    }

    if (statusFilter === 'current') {
      filteredTasks = filteredTasks.filter(
        (task) => String(task.Status || task.status || '').toLowerCase() !== 'completed'
      );
    }

    // Group tasks by FocusAreaName
    const grouped = filteredTasks.reduce((acc, task) => {
      const focusArea = task.FocusAreaName || 'Uncategorized';
      if (!acc[focusArea]) {
        acc[focusArea] = [];
      }
      
      // Format due date
      let dueDate = null;
      if (task.EndDate) {
        const date = new Date(task.EndDate);
        dueDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else if (task.DueDate) {
        const date = new Date(task.DueDate);
        dueDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }

      acc[focusArea].push({
        id: task.TaskID || `${task.Title}-${task.StartDate}`,
        backendTaskId: task.BackendTaskID,
        title: task.Title,
        description: task.Description,
        tag: task.FocusAreaName,
        difficulty: task.DifficultyLevel || 'Medium',
        status: task.Status || task.status || 'In Progress',
        due: dueDate,
        startDate: task.StartDate,
        endDate: task.EndDate
      });
      
      return acc;
    }, {});

    // Convert to sections array
    return Object.entries(grouped).map(([focusArea, taskList]) => ({
      key: focusArea.toLowerCase().replace(/\s+/g, '-'),
      title: focusArea,
      tasks: taskList
    }));
  }, [normalizedTasks, dateFilter, customDateStart, customDateEnd, focusAreaFilter, difficultyFilter, statusFilter]);

  // Fetch tasks from backend
  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get("/task/activetasks");
      
      if (Array.isArray(response.data)) {
        setTasks(response.data);
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch tasks on mount
  useEffect(() => {
    fetchTasks();
  }, []);

  const searchedSections = useMemo(() => {
    const query = String(searchTerm || '').trim().toLowerCase();
    if (!query) return sections;

    return sections
      .map((section) => {
        const filteredTasks = (section.tasks || []).filter((task) => {
          return [
            task.title,
            task.description,
            task.tag,
            task.difficulty,
            task.status,
            task.due
          ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query));
        });

        return {
          ...section,
          tasks: filteredTasks
        };
      })
      .filter((section) => section.tasks.length > 0);
  }, [sections, searchTerm]);

  // Filter tasks by active tab (when not "All Tasks")
  const visibleSections = useMemo(() => {
    if (!searchedSections?.length) return [];
    if (active === "All Tasks") return searchedSections;
    return searchedSections.filter((s) => s.title === active || s.key === active);
  }, [searchedSections, active]);

  // Summary counts
  const allTasks = useMemo(() => sections.flatMap((s) => s.tasks || []), [sections]);
  const total = allTasks.length;
  const completed = allTasks.filter((task) => String(task.status).toLowerCase() === 'completed').length;
  const open = Math.max(0, total - completed);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (dateFilter !== 'all') count += 1;
    if (focusAreaFilter !== 'all') count += 1;
    if (difficultyFilter !== 'all') count += 1;
    if (statusFilter !== 'current') count += 1;
    return count;
  }, [dateFilter, focusAreaFilter, difficultyFilter, statusFilter]);

  const handleSearch = (event) => {
    event.preventDefault();
  };

  const handleClearFilters = () => {
    setDateFilter('all');
    setFocusAreaFilter('all');
    setDifficultyFilter('all');
    setStatusFilter('current');
    setCustomDateStart('');
    setCustomDateEnd('');
  };

  const handleApplyFilters = () => {
    setShowFilters(false);
  };

  const toggle = (id) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleTaskStatusChange = async (task, nextStatus) => {
    const backendId = task.backendTaskId || task.id;
    if (!backendId) return;

    try {
      setUpdatingStatusTaskId(String(backendId));
      await api.patch(`/task/status/${backendId}`, { status: nextStatus });
      setTasks((prev) =>
        (Array.isArray(prev) ? prev : []).map((item) => {
          const itemId = getTaskIdentifier(item);
          if (String(itemId) !== String(backendId)) return item;
          return {
            ...item,
            Status: nextStatus
          };
        })
      );
      await fetchTasks();
    } catch (err) {
      console.error('Error updating task status:', err);
      window.alert(err?.response?.data?.error || 'Failed to update task status');
    } finally {
      setUpdatingStatusTaskId(null);
    }
  };

  // Handle task creation success
  const handleTaskCreated = (newTask) => {
    console.log('New task created:', newTask);
    fetchTasks();
  };

  const handleDeleteTask = async (taskId) => {
    if (!taskId) return;

    try {
      setDeletingTaskId(String(taskId));
      await api.delete('/task/delete_task', {
        data: { task_id: taskId }
      });
      setPendingDeleteTask(null);
      fetchTasks();
    } catch (err) {
      console.error('Error deleting task:', err);
      window.alert(err.response?.data?.error || 'Failed to delete task');
    } finally {
      setDeletingTaskId(null);
    }
  };

  const openDeleteModal = (task) => {
    setPendingDeleteTask(task);
  };

  const closeDeleteModal = () => {
    if (deletingTaskId) return;
    setPendingDeleteTask(null);
  };

  // Handle opening the add task modal
  const handleAddTask = () => {
    setShowAddTaskModal(true);
  };

  if (loading) {
    return <ActionItemsSkeleton isDarkMode={isDarkMode} />;
  }

  return (
    <>
      <div className={`min-h-screen ${isDarkMode ? 'bg-surface text-slate-100' : 'bg-surface text-slate-900'}`}>
        {/* Action Items Section */}
        <section className="max-w-6xl mx-auto px-4 py-6 md:py-8">
          {/* Header */}
          <header className="flex items-start md:items-center justify-between gap-4 mb-6">
            <div className="min-w-0">

            {pendingDeleteTask && (
              <div
                className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 px-4"
                onClick={closeDeleteModal}
              >
                <div
                  className={`w-full max-w-md rounded-2xl border shadow-2xl p-6 ${
                    isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                  }`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                      Delete Task
                    </h2>
                    <button
                      type="button"
                      onClick={closeDeleteModal}
                      disabled={Boolean(deletingTaskId)}
                      className={`rounded p-1 transition-colors ${
                        isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-100'
                      } disabled:opacity-50`}
                      aria-label="close delete confirmation"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Are you sure you want to delete this task?
                  </p>

                  <div className="flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={closeDeleteModal}
                      disabled={Boolean(deletingTaskId)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      } disabled:opacity-50`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteTask(pendingDeleteTask.id)}
                      disabled={deletingTaskId === String(pendingDeleteTask.id)}
                      className="px-4 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {deletingTaskId === String(pendingDeleteTask.id) ? 'Deleting...' : 'Yes'}
                    </button>
                  </div>
                </div>
              </div>
            )}
              <h1 className={`text-2xl md:text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Task Items</h1>
              <p className={isDarkMode ? 'text-slate-200' : 'text-slate-600'}>Manage and track your action items and tasks</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={handleAddTask}
                className="rounded-lg px-4 py-2 text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap bg-primary-700 hover:bg-primary-800 text-white"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Task
              </button>
            </div>
          </header>

          {/* Search and Filter Bar */}
          <div className={`rounded-2xl border shadow-sm p-4 mb-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  placeholder="Search tasks by title, description, focus area, status, or due date..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    isDarkMode ? 'border-slate-600 bg-slate-700 text-slate-100 placeholder:text-slate-400' : 'border-slate-300 bg-white text-black'
                  }`}
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                >
                  Search
                </button>
                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    showFilters || activeFiltersCount > 0
                      ? 'bg-primary-600 text-white'
                      : isDarkMode
                        ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  <Filter size={18} />
                  Filters
                  {activeFiltersCount > 0 && (
                    <span className="bg-white text-primary-600 px-2 py-0.5 rounded-full text-xs font-bold">
                      {activeFiltersCount}
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className={`rounded-2xl border shadow-sm p-6 mb-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>Advanced Filters</h3>
                <button
                  type="button"
                  onClick={() => setShowFilters(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Task View
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black bg-white'
                    }`}
                  >
                    <option value="current">Current Tasks (hide completed)</option>
                    <option value="all">All Tasks (include completed)</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Date
                  </label>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black bg-white'
                    }`}
                  >
                    <option value="all">All Dates</option>
                    <option value="today">Today</option>
                    <option value="thisWeek">This Week</option>
                    <option value="thisMonth">This Month</option>
                    <option value="custom">Custom Range</option>
                  </select>
                  {dateFilter === 'custom' && (
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        value={customDateStart}
                        onChange={(e) => setCustomDateStart(e.target.value)}
                        className={`px-3 py-2 rounded-lg border text-sm ${
                          isDarkMode
                            ? 'bg-slate-700 border-slate-600 text-slate-100'
                            : 'bg-white border-slate-200 text-slate-900'
                        }`}
                      />
                      <input
                        type="date"
                        value={customDateEnd}
                        onChange={(e) => setCustomDateEnd(e.target.value)}
                        className={`px-3 py-2 rounded-lg border text-sm ${
                          isDarkMode
                            ? 'bg-slate-700 border-slate-600 text-slate-100'
                            : 'bg-white border-slate-200 text-slate-900'
                        }`}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Focus Area
                  </label>
                  <select
                    value={focusAreaFilter}
                    onChange={(e) => setFocusAreaFilter(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black bg-white'
                    }`}
                  >
                    <option value="all">All Focus Areas</option>
                    {availableFocusAreas.map((area) => (
                      <option key={area} value={area}>{area}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Difficulty
                  </label>
                  <select
                    value={difficultyFilter}
                    onChange={(e) => setDifficultyFilter(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black bg-white'
                    }`}
                  >
                    <option value="all">All Difficulties</option>
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className={`px-4 py-2 font-medium ${isDarkMode ? 'text-slate-300 hover:text-slate-100' : 'text-slate-600 hover:text-slate-800'}`}
                >
                  Clear All
                </button>
                <button
                  type="button"
                  onClick={handleApplyFilters}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          {(activeFiltersCount > 0 || searchTerm) && (
            <div className="flex items-center gap-2 flex-wrap mb-6">
              <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Active filters:</span>
              {searchTerm && (
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary-600 text-white rounded-full text-sm font-medium shadow-sm">
                  Search: "{searchTerm}"
                  <button type="button" onClick={() => setSearchTerm('')} className="hover:bg-primary-700 rounded-full p-0.5">
                    <X size={14} />
                  </button>
                </span>
              )}
              {statusFilter !== 'current' && <span className="px-3 py-1 bg-primary-600 text-white rounded-full text-sm">Showing: All Tasks (incl. completed)</span>}
              {dateFilter !== 'all' && <span className="px-3 py-1 bg-primary-600 text-white rounded-full text-sm">Date: {dateFilter}</span>}
              {focusAreaFilter !== 'all' && <span className="px-3 py-1 bg-primary-600 text-white rounded-full text-sm">Focus Area: {focusAreaFilter}</span>}
              {difficultyFilter !== 'all' && <span className="px-3 py-1 bg-primary-600 text-white rounded-full text-sm">Difficulty: {difficultyFilter}</span>}
            </div>
          )}

          {/* Tabs */}
          {/* <Tabs tabs={tabs} active={active} setActive={setActive} isDarkMode={isDarkMode} /> */}

          <div className={`hidden md:grid grid-cols-7 gap-4 px-4 py-3 text-xs font-semibold uppercase border-b mb-4 ${
            isDarkMode ? 'text-slate-100 border-slate-700' : 'text-slate-500 border-slate-200'
          }`}>
              <div className="col-span-3">Task</div>
              <div>Focus Area</div>
              <div>Difficulty</div>
              <div>Due Date</div>
              <div>Status</div>
          </div>

          {/* Sections */}
          <main className="">
            {visibleSections.length === 0 && (
              <div className={`rounded-xl border border-dashed p-8 text-center ${
                isDarkMode 
                  ? 'border-slate-600 bg-slate-800 text-slate-400' 
                  : 'border-slate-300 bg-white text-slate-600'
              }`}>
                No tasks found. {activeFiltersCount > 0 && 'Try adjusting your filters or click "+ Add Task" to get started!'}
              </div>
            )}

            {visibleSections.map((section) => (
              <section className="mb-2" key={section.key || section.title}>
                {/* TABS
                <div className={`flex items-center gap-2 text-sm mb-2 select-none ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  <span className="-ml-1">▾</span>
                  <span className={`font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{section.title}</span>
                  <span className={`ml-auto rounded-full px-2 py-0.5 text-xs ${
                    isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {section.tasks?.length || 0} {(section.tasks?.length || 0) === 1 ? "task" : "tasks"}
                  </span>
                </div> */}
                {section.tasks?.length ? (
                  <>
                  {/* Mobile task cards */}
                  <div className="md:hidden space-y-2">
                    {section.tasks.map((t, idx) => {
                      const taskId = t.id || `${section.key}-${idx}`;
                      const isCompleted = String(t.status).toLowerCase() === 'completed';
                      return (
                        <div
                          key={taskId}
                          className={`rounded-xl border shadow-sm px-4 py-3.5 ${
                            isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <h3 className={`min-w-0 text-sm font-semibold leading-snug ${
                              isCompleted
                                ? isDarkMode ? 'text-slate-500 line-through' : 'text-slate-400 line-through'
                                : isDarkMode ? 'text-slate-100' : 'text-slate-900'
                            }`}>
                              {t.title}
                            </h3>
                            {hasBackendTasks && (
                              <button
                                type="button"
                                onClick={() => openDeleteModal({
                                  id: t.backendTaskId || taskId,
                                  title: t.title
                                })}
                                className={`shrink-0 rounded p-1 hover:bg-red-600 hover:text-white ${
                                  isDarkMode ? 'text-slate-400' : 'text-slate-400'
                                }`}
                                aria-label="delete task"
                              >
                                <X size={18} />
                              </button>
                            )}
                          </div>

                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full ${
                              isDarkMode ? 'bg-primary-900/50 text-primary-300' : 'bg-primary-50 text-primary-700'
                            }`}>
                              {t.tag || section.title}
                            </span>
                            <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full ${
                              isDarkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-50 text-amber-700'
                            }`}>
                              {t.difficulty || 'Medium'}
                            </span>
                            <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ${
                              isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
                            }`}>
                              Due {t.due || '—'}
                            </span>
                          </div>

                          <div className="mt-3">
                            <select
                              value={t.status || 'In Progress'}
                              onChange={(e) => handleTaskStatusChange(t, e.target.value)}
                              disabled={updatingStatusTaskId === String(t.backendTaskId || taskId)}
                              className={`w-full rounded-lg border px-3 py-2 text-sm font-medium ${
                                isDarkMode
                                  ? 'bg-slate-700 border-slate-600 text-slate-100'
                                  : 'bg-white border-slate-200 text-slate-800'
                              }`}
                            >
                              <option value="In Progress">In Progress</option>
                              <option value="Completed">Completed</option>
                            </select>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop task table */}
                  <div className="hidden md:block overflow-x-auto overflow-y-visible">
                    <div className="space-y-2 min-w-[760px]">
                      {section.tasks.map((t, idx) => {
                        const taskId = t.id || `${section.key}-${idx}`;
                        const isChecked = !!checked[taskId];
                        return (
                          <div
                            key={taskId}
                            className={`grid grid-cols-7 gap-4 items-center px-4 py-4 rounded-lg transition-colors ${
                              idx % 2 === 0
                                ? isDarkMode
                                  ? 'bg-slate-800/50 hover:bg-slate-800'
                                  : 'bg-white hover:bg-blue-50/50'
                                : isDarkMode
                                  ? 'bg-slate-800/30 hover:bg-slate-800'
                                  : 'bg-blue-50/20 hover:bg-blue-50/40'
                            }`}
                          >
                            <div className="col-span-3 min-w-0 flex items-center gap-3">
                              {hasBackendTasks && (
                                <button
                                  type="button"
                                  onClick={() => openDeleteModal({
                                    id: t.backendTaskId || taskId,
                                    title: t.title
                                  })}
                                  className={`shrink-0 rounded p-1 hover:bg-red-600 ${
                                    isDarkMode ? 'text-slate-300' : 'text-slate-400'
                                  }`}
                                  aria-label="delete task"
                                >
                                  <X size={18} />
                                </button>
                              )}
                              <span className={`truncate font-medium ${
                                String(t.status).toLowerCase() === 'completed'
                                  ? isDarkMode
                                    ? 'text-slate-500 line-through'
                                    : 'text-slate-400 line-through'
                                  : isDarkMode
                                    ? 'text-slate-100'
                                    : 'text-slate-900'
                              }`}>
                                {t.title}
                              </span>
                            </div>
                            <div className={`whitespace-nowrap ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                              {t.tag || section.title}
                            </div>
                            <div className={`whitespace-nowrap font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                              {t.difficulty || 'Medium'}
                            </div>
                            <div className={`whitespace-nowrap ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              {t.due || 'No due date'}
                            </div>
                            <div className={`whitespace-nowrap ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} flex items-center gap-2`}>
                              <select
                                value={t.status || 'In Progress'}
                                onChange={(e) => handleTaskStatusChange(t, e.target.value)}
                                disabled={updatingStatusTaskId === String(t.backendTaskId || taskId)}
                                className={`rounded border px-2 py-1 text-xs ${
                                  isDarkMode
                                    ? 'bg-slate-700 border-slate-600 text-slate-100'
                                    : 'bg-white border-slate-200 text-slate-800'
                                }`}
                              >
                                <option value="In Progress">In Progress</option>
                                <option value="Completed">Completed</option>
                              </select>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  </>
                ) : (
                  <div className={`rounded-xl border p-6 text-sm ${
                    isDarkMode ? 'border-slate-700 bg-slate-800 text-slate-400' : 'border-slate-200 bg-white text-slate-600'
                  }`}>No tasks in this section.</div>
                )}
              </section>
            ))}

            {/* Focus Area Summary */}
            <section className="mt-10">
              <div className={`rounded-2xl px-4 py-4 md:px-6 md:py-6 ${
                isDarkMode ? 'bg-slate-800 text-slate-200' : 'bg-primary-900 text-primary-50'
              }`}>
                <h4 className="text-sm/none font-semibold mb-4 opacity-90">Focus Area Summary</h4>
                <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
                  <div className={`rounded-xl px-4 py-5 ${
                    isDarkMode ? 'bg-slate-700/40' : 'bg-primary-800/40'
                  }`}>
                    <div className="text-xs uppercase tracking-wide opacity-80">Total Tasks</div>
                    <div className="mt-2 text-2xl font-bold">{total}</div>
                  </div>
                  <div className={`rounded-xl px-4 py-5 ${
                    isDarkMode ? 'bg-slate-700/40' : 'bg-primary-800/40'
                  }`}>
                    <div className="text-xs uppercase tracking-wide opacity-80">Open Tasks</div>
                    <div className="mt-2 text-2xl font-bold">{open}</div>
                  </div>
                  <div className={`rounded-xl px-4 py-5 ${
                    isDarkMode ? 'bg-slate-700/40' : 'bg-primary-800/40'
                  }`}>
                    <div className="text-xs uppercase tracking-wide opacity-80">Completed</div>
                    <div className="mt-2 text-2xl font-bold">{completed}</div>
                  </div>
                  <div className={`rounded-xl px-4 py-5 ${
                    isDarkMode ? 'bg-slate-700/40' : 'bg-primary-800/40'
                  }`}>
                    <div className="text-xs uppercase tracking-wide opacity-80">Due This Week</div>
                    <div className="mt-2 text-2xl font-bold">0</div>
                  </div>
                </div>
              </div>
            </section>
          </main>

          {/* Floating Action Button */}
          <button 
            type="button" 
            aria-label="quick add task" 
            onClick={handleAddTask}
            className={`fixed bottom-28 right-4 md:bottom-6 md:right-6 grid h-12 w-12 place-items-center rounded-full shadow-lg z-40 ${
              isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-amber-300 text-amber-900 hover:bg-amber-400'
            }`}
          >
            <span className="text-2xl leading-none">＋</span>
          </button>
        </section>
      </div>

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={showAddTaskModal}
        onClose={() => setShowAddTaskModal(false)}
        onTaskCreated={handleTaskCreated}
      />
    </>
  );
}

